import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import { Loader2, Building2, Globe, MapPin, KeyRound, CreditCard, CheckCircle, ExternalLink } from 'lucide-react';

/* ─── inline SVGs ─── */
const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 4l-6 6 6 6" />
  </svg>
);
const IconUpload = () => (
  <svg width="12" height="12" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="M10 13V4M6 7l4-4 4 4"/><path d="M3 15v1a2 2 0 002 2h10a2 2 0 002-2v-1"/>
  </svg>
);

/* ─── style helpers ─── */
const ghostBtn = "inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[#e4e1db] text-[#5a5a5a] text-[12px] font-medium hover:border-[#0e0e0e] hover:text-[#0e0e0e] transition-colors cursor-pointer";
const inkBtn   = "inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0e0e0e] text-white text-[12px] font-medium hover:bg-[#222] transition-colors disabled:opacity-50 cursor-pointer";
const inputCls = "w-full text-[14px] text-[#0e0e0e] border-[#e4e1db] placeholder:text-[#c0bdb8] focus-visible:ring-0 focus-visible:border-[#0e0e0e] focus-visible:shadow-[0_0_0_3px_rgba(14,14,14,0.06)]";

const EditBusinessProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profilePicture, setProfilePicture] = useState(null);
  const [requestingReset, setRequestingReset] = useState(false);
  const [billingStatus, setBillingStatus] = useState({ has_payment_method: false, card_last4: null, card_brand: null });
  const [settingUpBilling, setSettingUpBilling] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    company_name: '',
    description: '',
    website: '',
    location: ''
  });

  const config = {
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  };

  useEffect(() => {
    fetchProfile();
    fetchBillingStatus();
  }, []);

  useEffect(() => {
    if (searchParams.get('billing_setup') === 'success') {
      fetchBillingStatus();
      toast.success('Payment method saved!');
      navigate('/edit-business-profile', { replace: true });
    }
  }, [searchParams]);

  const fetchProfile = async () => {
    try {
      const userRes = await axios.get(`${API}/auth/me`, config);
      setProfilePicture(userRes.data.picture || null);
      const profileRes = await axios.get(`${API}/business-profiles/me`, config);
      setProfile({
        name: userRes.data.name || '',
        company_name: profileRes.data.company_name || userRes.data.company_name || '',
        description: profileRes.data.description || '',
        website: profileRes.data.website || '',
        location: profileRes.data.location || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingStatus = async () => {
    try {
      const response = await axios.get(`${API}/billing/status`, config);
      setBillingStatus(response.data);
    } catch (error) {
      console.error('Error fetching billing status:', error);
    }
  };

  const handleSetupBilling = async () => {
    setSettingUpBilling(true);
    try {
      const response = await axios.post(`${API}/billing/setup`, {}, config);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set up billing');
      setSettingUpBilling(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await axios.post(`${API}/billing/portal`, {}, config);
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open billing portal');
    }
  };

  const handleRequestPasswordReset = async () => {
    setRequestingReset(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, {}, config);
      toast.success('Password reset email sent! Check your inbox.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setRequestingReset(false);
    }
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be less than 10MB'); return; }

    setUploadingPicture(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/profile-picture`, formData, {
        ...config,
        headers: { ...config.headers, 'Content-Type': 'multipart/form-data' }
      });
      setProfilePicture(response.data.url);
      toast.success('Profile picture updated!');
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSave = async () => {
    if (!profile.name) { toast.error('Name is required'); return; }

    setSaving(true);
    try {
      await axios.put(`${API}/users/me`, { name: profile.name }, config);
      await axios.put(`${API}/business-profiles/me`, {
        company_name: profile.company_name,
        description: profile.description,
        website: profile.website,
        location: profile.location
      }, config);
      toast.success('Profile updated successfully!');
      if (refreshUser) refreshUser();
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f7f5f2]">
        <div className="text-[#a0a0a0] text-[13px]">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f5f2]">

      {/* ── Sticky Header ── */}
      <div className="bg-white border-b border-[#e4e1db] sticky top-0 z-10">
        <div className="px-5 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <Link
              to="/dashboard"
              className="w-8 h-8 rounded-full border border-[#e4e1db] flex items-center justify-center text-[#a0a0a0] hover:bg-[#f7f5f2] hover:text-[#0e0e0e] transition-colors"
            >
              <IconArrowLeft />
            </Link>
            <span className="font-display font-normal text-[20px] text-[#0e0e0e]">Edit Profile</span>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => navigate('/dashboard')}
              className={ghostBtn}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className={inkBtn}
              data-testid="save-business-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Saving...
                </>
              ) : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="px-5 md:px-10 py-6 max-w-full space-y-5 pb-20">

        {/* Profile Picture */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Profile Picture</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">This appears on your public profile and in search results.</p>
          <div className="flex items-center gap-5">
            {/* Preview */}
            <div className="relative w-[100px] h-[100px] rounded-[10px] bg-[#f7f5f2] border border-[#e4e1db] flex items-center justify-center flex-shrink-0 overflow-hidden">
              {profilePicture ? (
                <img
                  src={profilePicture.startsWith('/api') ? `${API.replace('/api', '')}${profilePicture}` : profilePicture}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <Building2 className="w-8 h-8 text-[#c0bdb8]" />
              )}
              {uploadingPicture && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            {/* Upload */}
            <div>
              <label className="cursor-pointer" style={{ display: 'block', marginBottom: '8px' }}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureUpload}
                  className="hidden"
                  disabled={uploadingPicture}
                  data-testid="business-picture-input"
                />
                <span className={ghostBtn}>
                  <IconUpload />
                  {profilePicture ? 'Change Picture' : 'Upload Picture'}
                </span>
              </label>
              <p className="text-[11px] text-[#c0bdb8]">Square image recommended. At least 400×400px. Max 10MB.</p>
            </div>
          </div>
        </div>

        {/* Account Security */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Account Security</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Manage your login credentials.</p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[14px] font-medium text-[#0e0e0e]">Password</p>
              <p className="text-[12px] text-[#a0a0a0] mt-0.5">Reset your account password via email</p>
            </div>
            <button
              onClick={handleRequestPasswordReset}
              disabled={requestingReset}
              className={ghostBtn}
              data-testid="business-reset-password-btn"
            >
              <KeyRound className="w-3 h-3" />
              {requestingReset ? 'Sending...' : 'Reset Password'}
            </button>
          </div>
        </div>

        {/* Payment Method */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Payment Method</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Save a payment method to speed up checkout when booking photographers.</p>
          {billingStatus.has_payment_method ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-[13px] font-medium text-[#16a34a] bg-[#f0fdf4] border border-[#bbf7d0] rounded-[10px] px-4 py-3">
                <CheckCircle className="w-4 h-4" />
                <span className="capitalize">{billingStatus.card_brand} ending in {billingStatus.card_last4}</span>
              </div>
              <button onClick={handleManageBilling} className={ghostBtn}>
                <ExternalLink className="w-3 h-3" />
                Manage Payment Method
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-[#f7f5f2] rounded-[10px] p-4">
                <ul className="text-[13px] text-[#a0a0a0] space-y-1.5">
                  <li>• Skip entering card details at every checkout</li>
                  <li>• Payments are securely handled by Stripe</li>
                  <li>• Your card info is never stored on our servers</li>
                  <li>• 5% platform fee applies per transaction (waived during beta)</li>
                </ul>
              </div>
              <button
                onClick={handleSetupBilling}
                disabled={settingUpBilling}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#635BFF] text-white text-[12px] font-medium hover:bg-[#5046e5] transition-colors disabled:opacity-50"
              >
                <CreditCard className="w-3 h-3" />
                {settingUpBilling ? 'Redirecting...' : 'Add Payment Method'}
              </button>
            </div>
          )}
        </div>

        {/* Personal Information */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Personal Information</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Your name as it appears to photographers.</p>
          <div>
            <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">
              Your Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              placeholder="John Doe"
              className={inputCls}
              data-testid="business-name-input"
            />
          </div>
        </div>

        {/* Business Information */}
        <div className="bg-white border border-[#e4e1db] rounded-[20px] p-7">
          <h2 className="font-display font-normal text-[20px] text-[#0e0e0e] tracking-[-0.3px] mb-1">Business Information</h2>
          <p className="text-[13px] text-[#a0a0a0] mb-6">Tell photographers about your company.</p>

          <div className="space-y-5">
            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Business / Organization Name</label>
              <div className="relative">
                <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#c0bdb8]" />
                <Input
                  value={profile.company_name}
                  onChange={(e) => setProfile({ ...profile, company_name: e.target.value })}
                  placeholder="Your Company Inc."
                  className={`${inputCls} pl-9`}
                  data-testid="business-company-input"
                />
              </div>
            </div>

            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Description</label>
              <Textarea
                value={profile.description}
                onChange={(e) => setProfile({ ...profile, description: e.target.value })}
                placeholder="Tell creatives about your business..."
                className={`${inputCls} min-h-[100px] resize-y`}
                rows={4}
                data-testid="business-description-input"
              />
            </div>

            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Website</label>
              <div className="relative">
                <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#c0bdb8]" />
                <Input
                  value={profile.website}
                  onChange={(e) => setProfile({ ...profile, website: e.target.value })}
                  placeholder="https://yourcompany.com"
                  className={`${inputCls} pl-9`}
                  data-testid="business-website-input"
                />
              </div>
            </div>

            <div>
              <label className="text-[12px] font-semibold text-[#0e0e0e] mb-1.5 block">Location</label>
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#c0bdb8]" />
                <Input
                  value={profile.location}
                  onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                  placeholder="New York, NY"
                  className={`${inputCls} pl-9`}
                  data-testid="business-location-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Bottom save */}
        <div className="flex justify-end gap-2.5">
          <button onClick={() => navigate('/dashboard')} className={ghostBtn}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={inkBtn}
          >
            {saving ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                Saving...
              </>
            ) : 'Save All Changes'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default EditBusinessProfile;
